import { Cite } from '@citation-js/core';
import '@citation-js/plugin-ris';
import '@citation-js/plugin-bibtex';
import Papa from 'papaparse';
import * as XLSX from 'xlsx';

import type {
  BibliographicMetadata,
  ParsedImportEntry,
  ImportFormat,
} from '@/features/sources/types';

// =========================================
// CSL-JSON types (citation-js internal format)
// =========================================

interface CSLAuthor {
  given?: string;
  family?: string;
  literal?: string;
}

interface CSLDateParts {
  'date-parts'?: number[][];
}

interface CSLEntry {
  id?: string;
  type?: string;
  title?: string;
  author?: CSLAuthor[];
  issued?: CSLDateParts;
  'container-title'?: string;
  DOI?: string;
  abstract?: string;
  keyword?: string;
  volume?: string;
  issue?: string;
  page?: string;
  publisher?: string;
  URL?: string;
  language?: string;
}

// =========================================
// Parse functions
// =========================================

/**
 * Parse a RIS or BIB file content and return structured entries.
 * Uses a lightweight custom parser for RIS (handles large files efficiently).
 */
export function parseReferences(
  content: string,
  format: ImportFormat
): ParsedImportEntry[] {
  if (format === 'ris') {
    return parseRISFast(content);
  }

  if (format === 'pubmed') {
    return parseMEDLINE(content);
  }

  // BIB: still use citation-js (typically smaller files)
  const cite = new Cite(content, { forceType: '@bibtex/text' });
  const cslEntries = cite.get({ type: 'json', style: 'csl' }) as CSLEntry[];

  // Pre-extract BibTeX entries once to avoid O(n²) re-extraction
  const bibEntries = extractAllBibTeXEntries(content);

  return cslEntries.map((entry, index) => ({
    id: entry.id ?? `import-${index}`,
    metadata: mapCSLToMetadata(entry),
    raw: bibEntries[index] ?? '',
    selected: true,
  }));
}

/**
 * Fast RIS parser that avoids citation-js overhead.
 * Handles large files (10k+ entries) without freezing.
 */
function parseRISFast(content: string): ParsedImportEntry[] {
  const entries: ParsedImportEntry[] = [];
  const blocks = content.split(/\nER\s+-/);

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i]!.trim();
    if (!block || !block.includes('TY  -')) continue;

    const raw = `${block}\nER  -`;
    const fields = new Map<string, string[]>();

    for (const line of block.split('\n')) {
      const match = line.match(/^([A-Z][A-Z0-9])\s\s-\s?(.*)/);
      if (match) {
        const tag = match[1]!;
        const value = match[2]!.trim();
        if (!fields.has(tag)) fields.set(tag, []);
        fields.get(tag)!.push(value);
      }
    }

    const get = (tag: string) => fields.get(tag)?.[0] ?? null;
    const getAll = (tag: string) => fields.get(tag) ?? [];

    const pyRaw = get('PY') || get('Y1') || get('DA');
    let pubYear: number | null = null;
    if (pyRaw) {
      const parsed = parseInt(pyRaw.split('/')[0] ?? '', 10);
      if (!isNaN(parsed)) pubYear = parsed;
    }

    const sp = get('SP');
    const ep = get('EP');
    const pages = sp ? (ep ? `${sp}-${ep}` : sp) : null;

    const metadata: BibliographicMetadata = {
      title: get('TI') || get('T1') || null,
      authors: getAll('AU').map((a) => a.trim()),
      publication_year: pubYear,
      journal: get('JO') || get('JF') || get('T2') || get('JA') || null,
      doi: get('DO') || null,
      abstract: get('AB') || null,
      keywords: getAll('KW'),
      volume: get('VL') || null,
      issue: get('IS') || null,
      pages,
      publisher: get('PB') || null,
      url: get('UR') || null,
      language: get('LA') || null,
      source_type: get('TY') || null,
    };

    entries.push({
      id: get('ID') || `import-${entries.length}`,
      metadata,
      raw,
      selected: true,
    });
  }

  return entries;
}

/**
 * Detect format from file extension.
 */
export function detectFormat(fileName: string): ImportFormat | null {
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'ris') return 'ris';
  if (ext === 'bib' || ext === 'bibtex') return 'bib';
  if (ext === 'nbib' || ext === 'txt') return 'pubmed';
  return null;
}

/**
 * Validate file content before parsing.
 * Returns null if valid, error message if invalid.
 */
export function validateImportFile(
  content: string,
  format: ImportFormat
): string | null {
  if (!content.trim()) {
    return 'File is empty';
  }

  if (format === 'ris') {
    // RIS files must contain at least one TY tag
    if (!content.includes('TY  -')) {
      return 'Invalid RIS file: missing TY (type) tag';
    }
  }

  if (format === 'bib') {
    // BIB files must contain at least one @ entry
    if (!/@\w+\s*\{/.test(content)) {
      return 'Invalid BibTeX file: no entries found';
    }
  }

  if (format === 'pubmed') {
    if (!content.includes('PMID-')) {
      return 'Invalid PubMed file: missing PMID tag';
    }
  }

  return null;
}

/**
 * Fast MEDLINE/PubMed parser for .nbib/.txt files.
 * MEDLINE format uses variable-length tags (PMID, TI, AB, AU, FAU, DP, etc.)
 * with continuation lines indented by spaces.
 */
function parseMEDLINE(content: string): ParsedImportEntry[] {
  const entries: ParsedImportEntry[] = [];

  // Split records by blank lines followed by PMID-
  const blocks = content.split(/\n\s*\n(?=PMID- )/);

  for (const block of blocks) {
    const trimmed = block.trim();
    if (!trimmed || !trimmed.startsWith('PMID-')) continue;

    // Parse tags with continuation lines
    const fields = new Map<string, string[]>();
    let currentTag = '';
    let currentValue = '';

    for (const line of trimmed.split('\n')) {
      // Tag line: TAG - value (tag is 2-4 uppercase chars)
      const tagMatch = line.match(/^([A-Z][A-Z0-9]{0,3})\s*-\s?(.*)/);
      if (tagMatch) {
        // Save previous field
        if (currentTag) {
          if (!fields.has(currentTag)) fields.set(currentTag, []);
          fields.get(currentTag)!.push(currentValue.trim());
        }
        currentTag = tagMatch[1]!;
        currentValue = tagMatch[2]!;
      } else if (line.startsWith('      ') || line.startsWith('\t')) {
        // Continuation line
        currentValue += ' ' + line.trim();
      }
    }
    // Save last field
    if (currentTag) {
      if (!fields.has(currentTag)) fields.set(currentTag, []);
      fields.get(currentTag)!.push(currentValue.trim());
    }

    const get = (tag: string) => fields.get(tag)?.[0] ?? null;
    const getAll = (tag: string) => fields.get(tag) ?? [];

    // Parse year from DP (e.g. "2020 Sep 15")
    let pubYear: number | null = null;
    const dp = get('DP');
    if (dp) {
      const parsed = parseInt(dp.split(/\s/)[0] ?? '', 10);
      if (!isNaN(parsed)) pubYear = parsed;
    }

    // Parse DOI from AID field (e.g. "10.1016/j.jep.2020.112967 [doi]")
    let doi: string | null = null;
    for (const aid of getAll('AID')) {
      if (aid.includes('[doi]')) {
        doi = aid.replace(/\s*\[doi\]\s*$/, '').trim();
        break;
      }
    }

    // Authors: prefer FAU (full names), fallback to AU
    const authors = getAll('FAU').length > 0
      ? getAll('FAU').map((a) => a.trim())
      : getAll('AU').map((a) => a.trim());

    const metadata: BibliographicMetadata = {
      title: get('TI') || null,
      authors,
      publication_year: pubYear,
      journal: get('JT') || get('TA') || null,
      doi,
      abstract: get('AB') || null,
      keywords: getAll('MH'),
      volume: get('VI') || null,
      issue: get('IP') || null,
      pages: get('PG') || null,
      publisher: null,
      url: null,
      language: get('LA') || null,
      source_type: get('PT') || null,
    };

    entries.push({
      id: get('PMID') || `pubmed-${entries.length}`,
      metadata,
      raw: trimmed.slice(0, 500),
      selected: true,
    });
  }

  return entries;
}

// =========================================
// Internal helpers
// =========================================

function mapCSLToMetadata(entry: CSLEntry): BibliographicMetadata {
  return {
    title: entry.title ?? null,
    authors: formatAuthors(entry.author),
    publication_year: extractYear(entry.issued),
    journal: entry['container-title'] ?? null,
    doi: entry.DOI ?? null,
    abstract: entry.abstract ?? null,
    keywords: parseKeywords(entry.keyword),
    volume: entry.volume ?? null,
    issue: entry.issue ?? null,
    pages: entry.page ?? null,
    publisher: entry.publisher ?? null,
    url: entry.URL ?? null,
    language: entry.language ?? null,
    source_type: entry.type ?? null,
  };
}

function formatAuthors(authors?: CSLAuthor[]): string[] {
  if (!authors || authors.length === 0) return [];

  return authors.map((author) => {
    if (author.literal) return author.literal;
    const parts = [author.family, author.given].filter(Boolean);
    return parts.join(', ');
  });
}

function extractYear(issued?: CSLDateParts): number | null {
  if (!issued?.['date-parts']?.[0]?.[0]) return null;
  const year = issued['date-parts'][0][0];
  return typeof year === 'number' ? year : null;
}

function parseKeywords(keyword?: string): string[] {
  if (!keyword) return [];
  // Keywords can be separated by semicolons, commas, or newlines
  return keyword
    .split(/[;,\n]/)
    .map((k) => k.trim())
    .filter(Boolean);
}

function extractAllBibTeXEntries(content: string): string[] {
  const entries: string[] = [];
  let depth = 0;
  let start = -1;

  for (let i = 0; i < content.length; i++) {
    if (content[i] === '@' && depth === 0) {
      start = i;
    }
    if (content[i] === '{') {
      depth++;
    }
    if (content[i] === '}') {
      depth--;
      if (depth === 0 && start !== -1) {
        entries.push(content.slice(start, i + 1));
        start = -1;
      }
    }
  }

  return entries;
}

/**
 * Parse CSV content into import entries.
 * Auto-detects common column headers.
 */
export function parseCSV(content: string): ParsedImportEntry[] {
  const result = Papa.parse<Record<string, string>>(content, {
    header: true,
    skipEmptyLines: true,
    transformHeader: (h) => h.trim(),
  });

  return result.data.map((row, index) => {
    const metadata = mapRowToMetadata(row);
    return {
      id: `csv-${index}`,
      metadata,
      raw: JSON.stringify(row).slice(0, 500),
      selected: true,
    };
  });
}

/**
 * Parse an Excel file (ArrayBuffer) into import entries.
 */
export function parseExcel(buffer: ArrayBuffer): ParsedImportEntry[] {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const firstSheet = workbook.SheetNames[0];
  if (!firstSheet) return [];

  const sheet = workbook.Sheets[firstSheet];
  if (!sheet) return [];

  const rows = XLSX.utils.sheet_to_json<Record<string, string>>(
    sheet,
    { defval: '' }
  );

  return rows.map((row, index) => {
    const metadata = mapRowToMetadata(row);
    return {
      id: `excel-${index}`,
      metadata,
      raw: JSON.stringify(row).slice(0, 500),
      selected: true,
    };
  });
}

/** Auto-detect common CSV/Excel column headers and map to metadata */
function mapRowToMetadata(row: Record<string, string>): BibliographicMetadata {
  const find = (candidates: string[]): string | null => {
    for (const key of Object.keys(row)) {
      const lower = key.toLowerCase().trim();
      if (candidates.includes(lower)) return row[key]?.trim() || null;
    }
    return null;
  };

  const findArray = (candidates: string[]): string[] => {
    const val = find(candidates);
    if (!val) return [];
    return val.split(/[;,]/).map((s) => s.trim()).filter(Boolean);
  };

  const yearStr = find(['year', 'publication_year', 'publication year', 'pub year', 'date', 'py']);
  let year: number | null = null;
  if (yearStr) {
    const parsed = parseInt(yearStr, 10);
    if (!isNaN(parsed) && parsed > 1000 && parsed < 3000) year = parsed;
  }

  return {
    title: find(['title', 'article title', 'document title', 'ti']),
    authors: findArray(['authors', 'author', 'au']),
    publication_year: year,
    journal: find(['journal', 'source title', 'source', 'journal title', 'publication name', 'jo', 'jf']),
    doi: find(['doi', 'digital object identifier']),
    abstract: find(['abstract', 'ab']),
    keywords: findArray(['keywords', 'author keywords', 'index keywords', 'keyword', 'kw']),
    volume: find(['volume', 'vol']),
    issue: find(['issue', 'number']),
    pages: find(['pages', 'page', 'art. no.', 'article number']),
    publisher: find(['publisher']),
    url: find(['url', 'link', 'source url']),
    language: find(['language', 'lang']),
    source_type: find(['document type', 'type', 'source type']),
  };
}

/**
 * Count total entries in a file without full parsing.
 */
export function countEntries(content: string, format: ImportFormat): number {
  if (format === 'ris') {
    return (content.match(/^TY\s\s-/gm) ?? []).length;
  }
  if (format === 'pubmed') {
    return (content.match(/^PMID-\s/gm) ?? []).length;
  }
  return (content.match(/@\w+\s*\{/g) ?? []).length;
}
